import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { Store } from '@ngrx/store';
import { ColDef } from 'ag-grid-community';
import { Observable, Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

import { NSEBillConstants } from '@ruby/configs/ebill.constants';
import { EBillTbtTocActions, fromEBillTbtToc } from '@ruby/modules/ebill/store/ebill-tbt-toc.index';
import { selectBillTbtTocState } from '@ruby/modules/ebill/store/selectors/ebill-tbt-toc.selectors';
import { ITransaction } from '@ruby/shared/models/commons/transaction.interface';
import { IEBillCarInfo } from '@ruby/shared/models/ebill/ebil.interface';

@Component({
  selector: 'ng-ebill-tbt-toc-container',
  template: `
    <ng-ebill-tbt-toc *ngIf="(store$ | async) as store"
                      [loading]="store.loading"
                      [details]="store.tbtDetails"
                      [invoice]="bill.billNumber"
                      [columns]="columns"
                      [isDownloading]="store.isDownloading"
                      (reload)="dispatchLoadAction()"
                      (downloadExcel)="dispatchDownloadAction()"
    >
    </ng-ebill-tbt-toc>`
})
export class EbillTbtTocContainer implements OnDestroy {
  bill!: IEBillCarInfo;
  store$: Observable<fromEBillTbtToc.State>;
  columns: Array<ColDef> = [];
  private acrSubscription!: Subscription;

  constructor(private store: Store<fromEBillTbtToc.State>, private acr: ActivatedRoute) {
    this.store$ = store.select(selectBillTbtTocState).pipe(tap((st: fromEBillTbtToc.State) => {
      if (st.tbtDetails && st.tbtDetails.customerNum && this.columns.length === 0) {
        this.loadColumns(st.tbtDetails.tierLevel);
      }
    }));
    this.parseTransaction();
  }

  ngOnDestroy(): void {
    this.acrSubscription.unsubscribe();
  }

  /**
   * Dispatch Load Action
   *
   * @summary Load Details
   * @returns void
   */
  dispatchLoadAction(): void {
    this.store.dispatch(EBillTbtTocActions.loadDetails({ data: this.bill }));
  }

  /**
   * Dispatch Download Excel Action
   *
   * @summary dispatch download Excel Action
   * @returns void
   */
  dispatchDownloadAction(): void {
    this.store.dispatch(EBillTbtTocActions.downloadExcel({
      data: {
        customerNumber: this.bill.customerNumber,
        freightBillNumber: this.bill.billNumber,
        demurrageDetailsActionType: `DOWNLOAD_ALL`
      }
    }));
  }

  private parseTransaction(): void {
    this.acrSubscription = this.acr.data.pipe(
      map((data: Data) => data.transaction),
      filter((param: ITransaction) => !!(param.parameters && param.parameters.eBill))
    ).subscribe((transaction: ITransaction) => {
      this.bill = JSON.parse(transaction.parameters.eBill!) as IEBillCarInfo;
      this.dispatchLoadAction();
    });
  }

  private loadColumns(tierLevel: Array<string>): void {
    const tierColumns: Array<ColDef> = [...NSEBillConstants.tbtTocDetailGridColumns];
    tierLevel.forEach((level: string) => {
      tierColumns.push({
        ...NSEBillConstants.eBillCurrencyColDef,
        headerName: `Tier ${ level } RATE`,
        field: `tierRate${ level }`
      });
      tierColumns.push({
        headerName: `DAYS AT Tier ${ level } RATE`,
        field: `tierDays${ level }`,
        headerClass: 'center-align',
        cellClass: 'justify-content-center'
      });
    });
    this.columns = tierColumns;
  }
}
